import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Stack, TextField, Typography, Alert, Paper, Fade } from '@mui/material';
import { toast } from 'react-toastify';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';
import { fileUploadApi } from '@/data/api/fileUpload';

const FILE_TYPES = Object.freeze({
  PDF: 'pdf',
  IMAGE: 'image',
  VIDEO: 'video',
});

const FILE_PATTERNS = Object.freeze({
  [FILE_TYPES.PDF]:   /\.pdf(\?.*)?$/i,
  [FILE_TYPES.IMAGE]: /\.(png|jpe?g|gif|webp|svg|bmp|tiff?)(\?.*)?$/i,
  [FILE_TYPES.VIDEO]: /\.(mp4|webm|ogv?|m3u8|mov|avi|mkv)(\?.*)?$/i,
});

const MUX_STREAM_HOST_RE = /stream\.mux\.com/i;

const UI = Object.freeze({
  DEFAULT_LABEL: 'File Upload',
  DEFAULT_PLACEHOLDER: 'Enter file URL or upload a file',
  BTN_UPLOAD: 'Upload',
  BTN_UPLOADING: 'Uploading...',
  BTN_CLEAR: 'Clear',
  PREVIEW_NOT_AVAILABLE: 'Preview not available for this file type',
  ERR_UPLOAD_FAILED: 'Upload failed. Please try again.',
});

const DEFAULT_PDF_COMPRESSION_QUALITY = 90;

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';


const isFileType = (url, type) => {
  if (!url || typeof url !== 'string') return false;
  if (type === FILE_TYPES.VIDEO && MUX_STREAM_HOST_RE.test(url)) return true;
  return FILE_PATTERNS[type]?.test(url) || false;
};

const validateUrl = (url) => {
  if (!url) return { isValid: false, error: null };
  try {
    new URL(url);
    return { isValid: true, error: null };
  } catch {
    return { isValid: false, error: 'Please enter a valid URL' };
  }
};


export const FileUploadWithManualInput = ({
  label = UI.DEFAULT_LABEL,
  accept = '*/*',
  currentUrl = '',
  onChange,
  placeholder = UI.DEFAULT_PLACEHOLDER,
  poster,
  maxFileSize = 50 * 1024 * 1024,
  disabled = false,


  validatePdfHead = false,   
  useBlobForPdf = false,     
  compressPdf = false,
  pdfCompressionQuality = DEFAULT_PDF_COMPRESSION_QUALITY,
}) => {
  const [fileUrl, setFileUrl] = useState(currentUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [urlError, setUrlError] = useState(null);

  // For blob/objectURL lifecycle
  const [blobUrl, setBlobUrl] = useState(null);
  const lastObjectUrlRef = useRef(null);

  const fileToUint8Array = async file => new Uint8Array(await file.arrayBuffer());

  const compressPdfFile = async (file, scale = 1.0, quality = 0.92) => {
    const sourceBytes = await fileToUint8Array(file);
    const pdf = await pdfjsLib.getDocument({ data: sourceBytes, useWorkerFetch: false, stopAtErrors: true }).promise;
    const compressedPdf = await PDFDocument.create();

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d', { alpha: false });

      if (!context) throw new Error('Unable to prepare canvas for PDF compression');

      canvas.width = Math.max(1, Math.ceil(viewport.width));
      canvas.height = Math.max(1, Math.ceil(viewport.height));

      await page.render({ canvasContext: context, viewport }).promise;

      const imageBytes = await fetch(canvas.toDataURL('image/jpeg', quality)).then(r => r.arrayBuffer());
      const embeddedImage = await compressedPdf.embedJpg(imageBytes);
      const pageToDraw = compressedPdf.addPage([canvas.width, canvas.height]);
      pageToDraw.drawImage(embeddedImage, { x: 0, y: 0, width: canvas.width, height: canvas.height });
    }

    const compressedBytes = await compressedPdf.save({ useObjectStreams: true });
    return new File([compressedBytes], file.name, { type: 'application/pdf' });
  };

  const compressPdfToLimit = async (file) => {
    const steps = [
      { scale: 1.0, quality: 0.98 },
      { scale: 1.0, quality: 0.95 },
      { scale: 1.0, quality: 0.90 },
      { scale: 0.90, quality: 0.70 },
      { scale: 0.85, quality: 0.65 },
    ];
    for (const { scale, quality } of steps) {
      const compressed = await compressPdfFile(file, scale, quality);
      if (compressed.size <= maxFileSize) return compressed;
    }
    return compressPdfFile(file, 0.85, 0.65);
  };

  useEffect(() => {
    setFileUrl(currentUrl);
  }, [currentUrl]);

  // Cleanup any object URLs we created
  useEffect(() => {
    return () => {
      if (lastObjectUrlRef.current) {
        URL.revokeObjectURL(lastObjectUrlRef.current);
        lastObjectUrlRef.current = null;
      }
    };
  }, []);

  // Optional: validate PDF via HEAD, or fetch as Blob for iframe
  useEffect(() => {
    let active = true;

    const clearBlobUrl = () => {
      if (lastObjectUrlRef.current) {
        URL.revokeObjectURL(lastObjectUrlRef.current);
        lastObjectUrlRef.current = null;
      }
      if (active) setBlobUrl(null);
    };

    clearBlobUrl();

    const maybePreparePdf = async () => {
      if (!fileUrl || !isFileType(fileUrl, FILE_TYPES.PDF)) return;

      try {
        if (validatePdfHead) {
          const head = await fetch(fileUrl, { method: 'HEAD' });
          const ctype = (head.headers.get('content-type') || '').toLowerCase();
          if (!ctype.includes('pdf')) {
            // We can still try to render; but warn the user
            toast.warn('File is not labeled as application/pdf; preview may fail.');
          }
        }

        if (useBlobForPdf) {
          const res = await fetch(fileUrl);
          const blob = await res.blob();
          const ensured = blob.type ? blob : new Blob([blob], { type: 'application/pdf' });
          const objUrl = URL.createObjectURL(ensured);
          if (!active) {
            URL.revokeObjectURL(objUrl);
            return;
          }
          lastObjectUrlRef.current = objUrl;
          setBlobUrl(objUrl);
        }
      } catch (err) {
        if (active) {
          setError('Failed to prepare PDF preview');
          toast.error('Failed to prepare PDF preview');
        }
      }
    };

    maybePreparePdf();
    return () => { active = false; };
  }, [fileUrl, validatePdfHead, useBlobForPdf]);

  const handleFileUpload = async (file) => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      let uploadFile = file;

      if (file.size > maxFileSize && isFileType(file.name, FILE_TYPES.PDF)) {
        uploadFile = await compressPdfToLimit(file);
        if (uploadFile.size > maxFileSize) {
          setError(`File size must be less than ${Math.round(maxFileSize / 1024 / 1024)}MB`);
          setLoading(false);
          return;
        }
      } else if (file.size > maxFileSize) {
        setError(`File size must be less than ${Math.round(maxFileSize / 1024 / 1024)}MB`);
        setLoading(false);
        return;
      } else if (compressPdf && isFileType(file.name, FILE_TYPES.PDF)) {
        uploadFile = await compressPdfToLimit(file);
      }

      const resp = await fileUploadApi(uploadFile);
      const url = resp?.data?.cdnLink || resp?.data?.file_link || resp?.data;
      if (url) {
        setFileUrl(url);
        onChange?.(url);
      } else {
        throw new Error(UI.ERR_UPLOAD_FAILED);
      }
    } catch (err) {
      setError(err?.message || UI.ERR_UPLOAD_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const pickFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = (event) => {
      const file = event.target.files?.[0];
      handleFileUpload(file);
    };
    input.click();
  };

  const handleManualChange = (e) => {
    const url = e.target.value.trim();
    setFileUrl(url);

    const { isValid, error } = validateUrl(url);
    setUrlError(error || null);

    if (isValid || !url) {
      onChange?.(url);
    }
  };

  const handleClear = () => {
    setFileUrl('');
    setError(null);
    setUrlError(null);
    onChange?.('');
  };

  const commonCardStyles = useMemo(
    () => ({
      boxShadow: '0 0.25rem 0.75rem rgba(0,0,0,0.1)',
      overflow: 'hidden',
    }),
    []
  );

  const renderPreview = () => {
    if (!fileUrl || urlError) return null;

    if (isFileType(fileUrl, FILE_TYPES.PDF)) {
      const src = useBlobForPdf && blobUrl ? blobUrl : fileUrl;
      return (
        <Paper elevation={2} sx={commonCardStyles}>
          <iframe
            src={src}
            title="PDF Preview"
            width="100%"
            style={{ border: 'none', display: 'block', height: '50vh', minHeight: '44rem' }}
            onError={(e) => toast.error(e?.message || 'Failed to load PDF preview')}
            onLoad={() => setError(null)}
          />
        </Paper>
      );
    }

    if (isFileType(fileUrl, FILE_TYPES.IMAGE)) {
      return (
        <Paper elevation={2} sx={commonCardStyles}>
          <img
            src={fileUrl}
            alt="Preview"
            style={{
              width: '100%',
              maxHeight: '25rem',
              objectFit: 'contain',
              display: 'block',
            }}
            onError={() => setError('Failed to load image preview')}
            onLoad={() => setError(null)}
          />
        </Paper>
      );
    }

    if (isFileType(fileUrl, FILE_TYPES.VIDEO)) {
      return (
        <Paper elevation={2} sx={commonCardStyles}>
          <video
            src={fileUrl}
            poster={poster}
            controls
            playsInline
            preload="metadata"
            style={{
              width: '100%',
              maxHeight: '25rem',
              display: 'block',
              outline: 'none',
            }}
            onError={() => setError('Failed to load video preview')}
            onLoadStart={() => setError(null)}
          />
        </Paper>
      );
    }

    return (
      <Paper
        elevation={1}
        sx={{
          p: 3,
          textAlign: 'center',
          bgcolor: 'grey.50',
          ...commonCardStyles,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {UI.PREVIEW_NOT_AVAILABLE}
        </Typography>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
      >
        {label}
      </Typography>

      <Stack spacing={3}>
        <Box sx={{ position: 'relative' }}>
          <TextField
            fullWidth
            placeholder={placeholder}
            value={fileUrl}
            style={{
              color: 'gray',
              border: '1px solid #ccc',
              borderRadius: '0.375rem',
              backgroundColor: '#f0f0f0'
            }}
            disabled={disabled || loading}
            error={!!urlError}
            helperText={urlError}
            InputProps={{
              endAdornment: (
                <Stack direction="row" spacing={1} sx={{ mr: 0.5 }}>
                  {fileUrl && (
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      onClick={handleClear}
                      disabled={disabled || loading}
                      sx={{
                        minWidth: '4rem',
                        height: '2rem',
                        borderRadius: '0.375rem',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                      }}
                    >
                      {UI.BTN_CLEAR}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={pickFile}
                    disabled={disabled || loading}
                    sx={{
                      minWidth: loading ? '7.5rem' : '5rem',
                      height: '2rem',
                      borderRadius: '0.375rem',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.15)',
                      '&:hover': { boxShadow: '0 0.25rem 0.75rem rgba(0,0,0,0.2)' },
                    }}
                  >
                    {loading ? UI.BTN_UPLOADING : UI.BTN_UPLOAD}
                  </Button>
                </Stack>
              ),
              sx: {
                borderRadius: '0.6rem',
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: 'primary.main' },
                },
              },
            }}
          />
        </Box>

        {error && (
          <Fade in={!!error}>
            <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: '0.5rem' }}>
              {error}
            </Alert>
          </Fade>
        )}

        {fileUrl && !urlError && (
          <Fade in={!!fileUrl}>
            <Box>{renderPreview()}</Box>
          </Fade>
        )}
      </Stack>
    </Box>
  );
};

export default FileUploadWithManualInput;
